import { DrawingModal } from "@/pages/inspections/components/DrawingModal";
import { buildUploadUrl, InspectionTemplate } from "@/shared/models/Inspection";
import { MESApiService } from "@dexteel/mesf-core";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	CircularProgress,
	IconButton,
	InputAdornment,
	TextField,
} from "@mui/material";
import { useState } from "react";

type Uploaded = {
	UploadFileId: number;
	Path: string | null;
};

type Props = {
	uploadFileId: number | null;
	fileName: string | null;
	templates: InspectionTemplate[];
	sectionName: string;
	disabled: boolean;
	onChange: (uploadFileId: number, fileName: string) => void;
	onError: (message: string) => void;
};

export const TemplateDrawingField = ({
	uploadFileId,
	fileName,
	templates,
	sectionName,
	disabled,
	onChange,
	onError,
}: Props) => {
	const [uploaded, setUploaded] = useState<Uploaded | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [hasFailed, setHasFailed] = useState(false);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);

	const isOwnUpload =
		uploaded !== null && uploaded.UploadFileId === uploadFileId;

	const attachedTo =
		templates.find(
			(template) =>
				uploadFileId !== null && template.UploadFileId === uploadFileId,
		) ?? null;

	const drawingUrl = buildUploadUrl(
		(isOwnUpload ? uploaded.Path : null) ?? attachedTo?.DrawingPath ?? null,
	);

	const label =
		fileName ??
		attachedTo?.DrawingFileName ??
		(uploadFileId === null ? "" : "Drawing attached");

	const upload = async (file: File) => {
		setIsUploading(true);
		setHasFailed(false);
		try {
			const response = await new MESApiService().uploadFiles(
				[file],
				"inspections",
			);
			const result = response?.result?.[0];

			if (!result) throw new Error("The drawing could not be uploaded");

			setUploaded({
				UploadFileId: result.UploadFileId,
				Path: (result as { Path?: string }).Path ?? null,
			});
			onChange(result.UploadFileId, result.FileName ?? file.name);
		} catch (uploadError) {
			setHasFailed(true);
			onError((uploadError as Error).message);
		} finally {
			setIsUploading(false);
		}
	};

	return (
		<>
			<TextField
				label="Drawing"
				value={label}
				placeholder="No drawing"
				disabled={disabled}
				fullWidth
				size="small"
				title={label}
				slotProps={{
					inputLabel: { shrink: true },
					htmlInput: {
						readOnly: true,
						style: { textOverflow: "ellipsis" },
					},
					input: {
						endAdornment: (
							<InputAdornment position="end">
								{isUploading && <CircularProgress size={16} />}
								{drawingUrl !== null && (
									<IconButton
										size="small"
										title="Preview drawing"
										onClick={() => setIsPreviewOpen(true)}
									>
										<VisibilityIcon fontSize="small" color="primary" />
									</IconButton>
								)}
								{!isUploading && isOwnUpload && (
									<CheckCircleIcon fontSize="small" color="success" />
								)}
								{!isUploading && hasFailed && (
									<ErrorIcon fontSize="small" color="error" />
								)}
								<IconButton
									component="label"
									size="small"
									title={
										uploadFileId
											? "Replace current drawing"
											: "Upload a drawing"
									}
									disabled={disabled || isUploading}
								>
									<UploadFileIcon fontSize="small" />
									<input
										hidden
										type="file"
										accept="image/*,application/pdf"
										onChange={(event) => {
											const file = event.target.files?.[0];
											event.target.value = "";
											if (file) upload(file);
										}}
									/>
								</IconButton>
							</InputAdornment>
						),
					},
				}}
			/>
			<DrawingModal
				open={isPreviewOpen}
				url={drawingUrl}
				sectionName={sectionName}
				fileName={label || null}
				onClose={() => setIsPreviewOpen(false)}
			/>
		</>
	);
};
